<template>
  <v-data-table
    v-bind="$attrs"
    :items="internalFiles"
    :headers="headers"
    :no-data-text="$t('fresh-vue-files.FileGallery.no-data')"
  >
    <!-- Name -->
    <template #[`item.name`]="{ item }">
      <a
        v-if="item.url_show || item.url"
        :href="item.url_show || item.url"
        target="_blank"
      >
        {{ item.name }}
      </a>

      <span v-else>{{ item.name }}</span>
    </template>

    <!-- Dokumentenart -->
    <template #[`item.mimetype`]="{ item }">
      <v-tooltip left>
        <template #activator="{ on }">
          <v-icon v-on="on">{{ getFileTypeIcon(item.mimetype) }}</v-icon>
        </template>
        <span>{{ getFileTypeText(item.mimetype) }}</span>
      </v-tooltip>
    </template>

    <!-- Dokumentenkategorie -->
    <template v-if="fileCategories?.length > 0" #[`item.category`]="{ item }">
      <v-tooltip left>
        <template #activator="{ on }">
          <ValidationObserver ref="observer" v-on="on">
            <ValidationProvider name="Dokumentenkategorie">
              <v-select
                item-text="text"
                item-value="value"
                :value="getTabellenwertFileCategory(item)"
                :items="fileCategories"
                return-object
                clearable
                @change="saveItem(item, $event)"
              ></v-select>
            </ValidationProvider>
          </ValidationObserver>
        </template>
      </v-tooltip>
    </template>

    <!-- Erstellungszeitpunkt -->
    <template #[`item.created_at`]="{ item }">
      <v-tooltip bottom :disabled="!item.created_at">
        <template #activator="{ on }">
          <span v-on="on">
            {{ item.created_at | date }}
            <br />
            {{ item.created_at | time }}
          </span>
        </template>

        <span>Hochgeladen am: {{ item.created_at | dateAndTime }}</span>
      </v-tooltip>
    </template>

    <!-- Optionen -->
    <template #[`item.action`]="{ item }">
      <list-options :actions="actions" :data="{ item }" no-menu></list-options>
    </template>
  </v-data-table>
</template>

<script>
import _ from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import { AlertUtils } from '@freshsolutions/fresh-vue'

// import { VDataTable, VTooltip, VIcon } from 'vuetify/lib'
import { ListOptions } from '@freshsolutions/fresh-vue'

export default {
  name: 'FileGallery',

  components: {
    // VDataTable,
    // VTooltip,
    // VIcon,
    ListOptions
  },

  props: {
    /**
     * [
     *  {
     *    id: '1',
     *    mimetype: 'application/pdf',
     *    url: 'https://1.pdf'
     *  },
     *  {
     *    id: '2',
     *    mimetype: 'application/pdf',
     *    url: 'https://2.pdf'
     *  }
     * ]
     */
    files: {
      type: Array,
      required: false,
      default: () => []
    },

    deletable: {
      type: Boolean,
      required: false,
      default: false
    },

    doNotRemoveFilesFromInternalCacheOnDelete: {
      type: Boolean,
      required: false,
      default: false
    },

    fileCategories: {
      type: [String, Array],
      required: false,
      default: null
    }
  },

  data() {
    return {
      internalFiles: [],
      tabellenwertFileCategory: null
    }
  },

  computed: {
    headers() {
      let items = []

      items.push({
        text: this.$t('fresh-vue-files.FileGallery.header-name'),
        value: 'name'
      })
      items.push({
        text: this.$t('fresh-vue-files.FileGallery.header-extension'),
        value: 'extension'
      })
      items.push({
        text: this.$t('fresh-vue-files.FileGallery.header-mimetype'),
        value: 'mimetype',
        align: 'center'
      })
      if (_.get(this.fileCategories, 'length', 0) > 0) {
        items.push({
          text: this.$t('fresh-vue-files.FileGallery.header-category'),
          value: 'category',
          align: 'center'
        })
      }
      items.push({
        text: this.$t('fresh-vue-files.FileGallery.header-created_at'),
        value: 'created_at',
        align: 'center'
      })
      items.push({
        text: this.$t('fresh-vue-files.FileGallery.header-action'),
        value: 'action',
        align: 'center',
        sortable: false
      })

      return items
    },

    actions() {
      return [
        {
          icon: '$freshvuefiles-show',
          text: this.$t('fresh-vue-files.FileGallery.action-show'),
          action: this.onShow,
          hidden: ({ item }) => !item.url_show && !item.url
        },
        {
          icon: '$freshvuefiles-download',
          text: this.$t('fresh-vue-files.FileGallery.action-download'),
          action: this.onDownload,
          hidden: ({ item }) => !item.url_download && !item.url
        },
        {
          icon: '$freshvuefiles-delete',
          text: this.$t('fresh-vue-files.FileGallery.action-delete'),
          hidden: !this.deletable,
          action: this.onDelete
        }
      ]
    }
  },

  watch: {
    files: {
      handler: function (newFiles, oldFiles) {
        if (!_.isEqual(newFiles, oldFiles)) {
          // remove all falsy values (null, undefined, ...)
          this.internalFiles = (newFiles || []).map((item) => ({
            ...item,
            id: item.id ?? uuidv4()
          }))
        }
      },
      immediate: true
    }
  },

  methods: {
    onShow(event, { item }) {
      window.open(item.url_show || item.url, '_blank')
    },

    onDownload(event, { item }) {
      window.open(item.url_download || item.url, '_blank')
    },

    onDelete(event, { item: file }) {
      AlertUtils.fireQuestion({
        text: this.$t('fresh-vue-files.FileGallery.delete-confirmation')
      }).then((result) => {
        if (result.value) {
          if (!this.doNotRemoveFilesFromInternalCacheOnDelete) {
            const index = this.internalFiles.findIndex(
              (item) => item.id === file.id
            )
            this.internalFiles.splice(index, 1)
          }

          this.$emit('delete', file)
        }
      })
    },

    getTabellenwertFileCategory(item) {
      for (let fileCategory of this.fileCategories) {
        if (fileCategory.value === _.get(item, 'pivot.category')) {
          return fileCategory
        }
      }
      return null
    },

    saveItem(item, tabellenwertFileCategory) {
      this.$emit('saveItem', item, tabellenwertFileCategory)
    },

    getFileTypeIcon(mimetype) {
      switch (mimetype) {
        // normal mimetypes. We can use the icons directly
        case 'application/pdf':
        case 'image/jpeg':
        case 'image/png':
        case 'text/csv':
        case 'video/mp4':
          return `$freshvuefiles-file-${mimetype}`
        // ZIP
        case 'application/zip':
        case 'application/gzip':
        case 'application/x-7z-compressed':
          return `$freshvuefiles-file-application/pdf`
        // PowerPoint
        case 'application/mspowerpoint':
          return '$freshvuefiles-file-application/mspowerpoint'
        // Word
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          return '$freshvuefiles-file-application/msword'
        // Excel
        case 'application/msexcel':
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return '$freshvuefiles-file-application/msexcel'
        // Email
        case 'application/vnd.ms-outlook':
          return '$freshvuefiles-file-email'
        // if we don't know the mimetype, we return the default icon
        default:
          '$freshvuefiles-file-default'
      }
    },

    getFileTypeText(mimetype) {
      switch (mimetype) {
        // PDF
        case 'application/pdf':
          return 'PDF'
        // ZIP
        case 'application/zip':
        case 'application/gzip':
        case 'application/x-7z-compressed':
          return 'ZIP-Archiv'
        // Images
        case 'image/jpeg':
        case 'image/png':
          return 'Bild'
        // PowerPoint
        case 'application/mspowerpoint':
          return 'PowerPoint'
        // Word
        case 'application/msword':
        case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
          return 'Word'
        // Excel
        case 'application/msexcel':
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          return 'Excel'
        // CSV
        case 'text/csv':
          return 'CSV'
        // Video
        case 'video/mp4':
          return 'Video'
        // Email
        case 'application/vnd.ms-outlook':
          return 'E-Mail'
        default:
          'Unbekannt'
      }
    }
  }
}
</script>
